import { useAuthMe } from '@/features/user';
import { useLocaleWithUsd } from '@/stores';
import dayJS from '@/lib/dayjs';
import Image from 'next/image';
import Link from 'next/link';
import ShimmerImage from '@/components/ui/ShimmerImage';
import dayjs from 'dayjs';
import { displaySalary } from '@/lib/utils/formatters';
import { useRouter } from 'next/navigation';
import { AuthTokenStorageService } from '@/lib/auth';
import { PublicJobListDTO } from '@/shared/job/job.public.dto';
import JobSaveButton from './JobSaveButton';

type JobListItemProps = {
  job: PublicJobListDTO & { matchLevel?: number };
  showMatchLevel?: boolean;
};

const JobListItem = (props: JobListItemProps) => {
  const { job, showMatchLevel = false } = props;
  const { data: user } = useAuthMe();
  const isMobile = AuthTokenStorageService.checkIsMobile();
  const router = useRouter();
  const { locale, usdRate } = useLocaleWithUsd();
  const isDisabilityFriendly = Number(job.forHbi ?? 0) > 0;

  const disabilityFriendlyBadge = isDisabilityFriendly ? (
    <span className="inline-flex w-max rounded-full bg-primary-50 px-2 py-0.5 text-[10px] font-medium leading-none text-[#003C77]">
      ХБИ боломжтой
    </span>
  ) : null;
  const isFeatured =
    job.isFeatured && job.isFeatured > Math.floor(Date.now() / 1000);
  const isGolden = job.mlevel === 4;
  return (
    <div
      key={job.id}
      className={`px-4 py-3 flex items-center gap-4 border-t border-black/15 relative ${isFeatured ? 'bg-amber-50 hover:bg-amber-100' : isGolden ? 'hover:bg-amber-100' : 'hover:bg-gray-50'}`}
    >
      {isGolden && (
        <Image
          src="/images/icons/featured.svg"
          width={24}
          height={24}
          className="absolute left-0 top-0"
          alt={''}
        />
      )}
      {showMatchLevel && (
        <div className="absolute right-0 top-0 w-4 h-4 flex justify-center items-center bg-amber-50 text-xs text-gray-400">
          {job?.matchLevel ?? 0}
        </div>
      )}
      <Link
        href={`/company/${job.company?.alias || job.company?.id}`}
        prefetch={false}
        className="hidden rounded-lg w-16 h-16 lg:flex items-center justify-center bg-white"
        onClick={() => {
          sessionStorage?.setItem('jobs-scroll', window.scrollY.toString());
        }}
      >
        <ShimmerImage
          src={job.company?.logo ?? ''}
          alt={job.company?.name ?? 'company'}
          width={64}
          height={64}
          objectType="company"
          objectID={job.company?.id ?? 0}
          style={{
            objectFit: 'contain',
            width: 64,
            height: 64,
          }}
        />
      </Link>
      <div
        className="flex justify-between flex-1 cursor-pointer"
        onClick={() => {
          if (isMobile) {
            window.ReactNativeWebView?.postMessage(
              JSON.stringify({
                type: 'viewCompany',
                companyId: job.company?.id ?? 0,
              })
            );
          } else {
            sessionStorage?.setItem('jobs-scroll', window.scrollY.toString());
            router.push(`/job/${job.code}`);
          }
        }}
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            {isMobile ? (
              <>
                <button
                  onClick={() => {
                    window.ReactNativeWebView?.postMessage(
                      JSON.stringify({ type: 'viewJob', code: job })
                    );
                  }}
                  className="text-sm leading-none tracking-normal text-left text-black"
                >
                  {job.title}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.ReactNativeWebView?.postMessage(
                      JSON.stringify({
                        type: 'viewCompany',
                        companyId: job.company?.id ?? 0,
                      })
                    );
                  }}
                  className="inline-block max-w-max text-xs text-[#003C77] leading-[14px] tracking-normal text-left"
                >
                  {job.company?.name ?? 'company'}
                </button>
                {disabilityFriendlyBadge}
              </>
            ) : (
              <>
                <div className="text-sm leading-none tracking-normal uppercase transition-colors duration-150 hover:text-[#003C77]">
                  {job.title}
                </div>

                <Link
                  href={`/company/${job.company?.alias || job.company?.id}`}
                  prefetch={false}
                  className="inline-block max-w-max text-xs text-[#003C77] leading-[14px] tracking-normal"
                  onClick={(e) => {
                    e.stopPropagation();
                    sessionStorage?.setItem(
                      'jobs-scroll',
                      window.scrollY.toString()
                    );
                  }}
                >
                  {job.company?.name ?? 'company'}
                </Link>
                {disabilityFriendlyBadge}
              </>
            )}
          </div>
          <div className="flex flex-row items-center">
            <p
              className={`text-sm leading-none tracking-normal ${job.salary?.phrase === '' ? '' : 'mr-1'}`}
            >
              <span className="notranslate" translate="no">
                {displaySalary({
                  timeType: Number(job.timetype),
                  timeSalary: job.timeSalary,
                  salary: job.salary?.phrase,
                  changeable: job.changeable,
                  usdRate: locale === 'en' ? (usdRate ?? 0) : undefined,
                  salaryId: job.salary?.id ? Number(job.salary?.id) : undefined,
                })}
              </span>
            </p>
          </div>
        </div>
        <div className="flex flex-row items-center gap-10 shrink-0">
          <div className="flex flex-col items-end w-auto">
            <div className="flex gap-2 items-center">
              {(user?.mlevel ?? 0) >= 5 && (
                <div className="text-[12px] text-[#28292A]/[72]">
                  CV:{job.cvCnt}
                </div>
              )}
              <JobSaveButton jobId={Number(job.id)} />
            </div>
            <p className="text-[10px] text-[#28292A]/[72] text-right">
              {job.location ?? ''}
            </p>
            <p className="text-[10px] text-[#28292A]/[72]">
              {dayJS.unix(Number(job.sortTime)).diff(dayjs(), 'hours') > -24
                ? dayJS.unix(Number(job.sortTime)).fromNow()
                : dayJS.unix(Number(job.sortTime)).format('YYYY-MM-DD HH:mm')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
export default JobListItem;
